import type { CustomerRevenueReportItem } from '@vy/types';
import type { Workbook, Worksheet } from 'exceljs';
import dayjs from 'dayjs';
import {
  getCustomerRevenueProductName,
  ROW_LEVELS,
  type ExpandedCustomerRevenueReportRow,
} from '../../../customer-revenue-report-page/customer-revenue-report-components/CustomerRevenueReportTable/customer-revenue-report-row-expander';
import {
  getNumericRevenueForDate,
  parseDateHeader,
} from '../../../customer-revenue-report-page/customer-revenue-report-components/CustomerRevenueReportTable/customer-revenue-report-calculations';

const REVENUE_LEAF_TYPES = ['scheduledCharge', 'revenue', 'deferredRevenue', 'unbilledRevenue'] as const;

type RevenueLeafType = (typeof REVENUE_LEAF_TYPES)[number];

const REVENUE_LEAF_LABELS: Record<RevenueLeafType, string> = {
  scheduledCharge: 'Billed',
  revenue: 'Revenue',
  deferredRevenue: 'Deferred',
  unbilledRevenue: 'Unbilled',
};

const RAW_DATA_HEADERS = ['Customer', 'Product', 'Revenue Type', 'Currency', 'Year', 'Month', 'Period', 'Amount'] as const;

export function buildRawDataSheet(
  workbook: Workbook,
  searchedItems: CustomerRevenueReportItem[],
  dateHeaders: string[],
): void {
  const worksheet = workbook.addWorksheet('Raw Data');

  writeRawDataHeader(worksheet);
  writeRawDataRows(worksheet, searchedItems, dateHeaders);
  applyRawDataColumnStyles(worksheet);
}

function writeRawDataHeader(worksheet: Worksheet): void {
  worksheet.addRow([...RAW_DATA_HEADERS]);
  worksheet.getRow(1).font = { bold: true };
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: RAW_DATA_HEADERS.length },
  };
}

function writeRawDataRows(
  worksheet: Worksheet,
  searchedItems: CustomerRevenueReportItem[],
  dateHeaders: string[],
): void {
  for (const item of searchedItems) {
    for (const revenueLeafType of REVENUE_LEAF_TYPES) {
      writeRawDataRowsForLeafType(worksheet, item, revenueLeafType, dateHeaders);
    }
  }
}

function writeRawDataRowsForLeafType(
  worksheet: Worksheet,
  item: CustomerRevenueReportItem,
  revenueLeafType: RevenueLeafType,
  dateHeaders: string[],
): void {
  const syntheticRow = buildSyntheticLeafRow(item, revenueLeafType);

  for (const dateHeader of dateHeaders) {
    const amount = getNumericRevenueForDate(syntheticRow, dateHeader);
    if (amount === 0) continue;

    const excelRow = worksheet.addRow(buildRawDataRowValues(item, revenueLeafType, dateHeader, amount));
    excelRow.commit();
  }
}

function buildRawDataRowValues(
  item: CustomerRevenueReportItem,
  revenueLeafType: RevenueLeafType,
  dateHeader: string,
  amount: number,
): (string | number)[] {
  const headerDate = dayjs(parseDateHeader(dateHeader));

  return [
    item.customer.name,
    getCustomerRevenueProductName(item, '—'),
    REVENUE_LEAF_LABELS[revenueLeafType],
    item.customer.currency ?? '',
    headerDate.year(),
    headerDate.format('MMMM'),
    headerDate.format('MMM YYYY'),
    amount,
  ];
}

function buildSyntheticLeafRow(
  item: CustomerRevenueReportItem,
  revenueLeafType: RevenueLeafType,
): ExpandedCustomerRevenueReportRow {
  return {
    ...item,
    revenueType: revenueLeafType,
    level: ROW_LEVELS.RevenueType,
    id: `${item.customer.id}-${revenueLeafType}`,
    isParent: false,
    isExpanded: false,
  };
}

function applyRawDataColumnStyles(worksheet: Worksheet): void {
  const widths = [28, 28, 18, 12, 10, 14, 14, 16];
  for (let index = 0; index < widths.length; index++) {
    worksheet.getColumn(index + 1).width = widths[index];
  }
  const amountColumn = worksheet.getColumn(RAW_DATA_HEADERS.length);
  amountColumn.numFmt = '#,##0.00';
  amountColumn.alignment = { horizontal: 'right' };
}
